import {
    ApolloClient,
    ApolloLink,
    from,
    HttpLink,
    InMemoryCache,
    NormalizedCacheObject,
    OperationVariables,
    QueryOptions,
    split,
} from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { ConversationsSelect } from './graphql/queries/conversations.query';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import {
    RegisterArgs,
    RegisterSelect,
} from './graphql/mutations/register.mutation';
import {
    FetchPolicy,
    MutationOptions,
} from '@apollo/client/core/watchQueryOptions';
import { ErrorCodesEnum } from './types';
import {
    AddFavoriteMutationVariables,
    AddFriendMutationVariables,
    BatchPayload,
    BlockProfileMutationVariables,
    ChangeFriendshipMutationVariables,
    Conversation,
    ConversationQueryVariables,
    ConversationsQueryVariables,
    DeleteGalleryImageInput,
    Favorite,
    FavoritesQueryVariables,
    Friendship,
    FriendshipsQueryVariables,
    FriendshipStatus,
    Gift,
    GiftInput,
    GiftsQueryVariables,
    HideConversationMutationVariables,
    Image,
    LoginMutationVariables,
    LoginResult,
    MessageInput,
    MessageType,
    MyBlockedWhereUniqueInput,
    MyProfile,
    MyProfileQueryVariables,
    NotificationCountQueryVariables,
    NotificationsQueryVariables,
    NotificationWhereUniqueInput,
    Offer,
    OffersQueryVariables,
    PictureInput,
    PokeInput,
    Profile,
    ProfileQueryVariables,
    ProfilesQueryVariables,
    ProfileWhereUniqueInput,
    Provider,
    ProvidersQueryVariables,
    Query,
    QueryFindManyConversationArgs,
    QueryFindManyFavoriteArgs,
    QueryFindManyFriendshipArgs,
    QueryFindManyGiftArgs,
    QueryFindManyNotificationArgs,
    QueryFindManyNotificationCountArgs,
    QueryFindManyOfferArgs,
    QueryFindManyProfileArgs,
    QueryFindManyProviderArgs,
    QueryFindManySupportTicketArgs,
    QueryFindManyVisitArgs,
    QueryFindOneConversationArgs,
    QueryFindOneProfileArgs,
    QueryFindOneSupportTicketArgs,
    RedeemCouponMutationVariables,
    RegisterMutationVariables,
    RegisterResult,
    RemoveFavoriteMutationVariables,
    RemoveFriendMutationVariables,
    ResetPasswordInput,
    ResetPasswordResult,
    ResultError,
    Scalars,
    SendMessageResult,
    SupportTicket,
    SupportTicketQueryVariables,
    SupportTicketsQueryVariables,
    TransactionInput,
    TransactionMutationVariables,
    TransactionResult,
    UnblockProfileMutationVariables,
    UpdateMyProfileInput,
    UpdateMyProfileMutationVariables,
    UpdateMyProfileResult,
    UpdateProfileImageInput,
    UploadGalleryImageInput,
    Visit,
    VisitProfileMutationVariables,
    VisitProfileResult,
    VisitQueryVariables,
    DeleteAccountInput,
    DeleteAccountResult,
    DeleteAccountMutationVariables,
    QueryAggregateConversationsArgs,
    AggregateConversationsResult,
    AggregateConversationsQueryVariables,
    ForgottenPasswordMutationVariables,
    ReadConversationMutationVariables,
    UpdateGalleryImageInput,
    AddFriendshipResult,
    SupportMessageCreateInput,
    SupportMessage,
    SendSupportMessageMutationVariables,
    ContactSupportMutationVariables,
    ContactSupportInput,
    Config,
    MutationVisitProfileArgs,
    UserUpdateInput,
    User,
    UpdateUserMutationVariables,
    QueryFindManyInterestCategoryArgs,
    InterestCategory,
    InterestCategoriesQueryVariables,
    Theme,
    ThemesQueryVariables,
    Mutation,
    CoinsResult,
    Notification,
    SupportMessageWhereUniqueInput,
    ReadSupportMessageMutationVariables,
    QueryFindManyProfileCountArgs,
    ProfileCountQueryVariables,
    UserComplain,
    UserComplainMutationVariables,
    UserComplainCreateInput,
    ComplainType,
    ComplainTypesQueryVariables,
    Language,
    SocialLoginInput,
    SocialLoginResult,
    FacebookLoginMutationVariables,
    GoogleLoginMutationVariables,
    QueryFindManyBlockedArgs,
    Blocked,
    BlockedQueryVariables,
    Match,
    MatchesQueryVariables,
    QueryFindManyMatchArgs,
    QueryFindManyMatchCountArgs,
    MatchCountQueryVariables,
    QueryFindManyBlockedCountArgs,
    BlockedCountQueryVariables,
    QueryFindManyFavoriteCountArgs,
    FavoritesCountQueryVariables,
    QueryFindManyFriendshipCountArgs,
    FriendshipsCountQueryVariables,
    MatchFilter,
    MatchFilterUpdateInput,
    MatchFilterResult,
    ChangeMatchFilterMutationVariables,
    ValidateResult,
    EmailMutationVariables,
    UsernameMutationVariables,
    MatchConfigResult,
    LoginByTokenMutationVariables,
    MobileNumberInput,
    UpdateMobileNumberMutationVariables,
    ValidateMobileNumberMutationVariables,
    NextMatchResult,
    SendMatchMutationVariables,
    MutationSendMatchArgs,
    Flag,
    QueryFindManyPictureArgs,
    Picture,
    PicturesQueryVariables,
    UploadPictureInput,
    UserPremiumStatus,
    BlockProfileInput,
    ValidateMobileNumberInput,
    ValidateEmailInput,
    UpdateEmailMutationVariables,
    Result,
    MobileNumberValidationInput,
    MobileNumberValidationMutationVariables,
    Transaction,
    ErrorCodes,
    QueryFindOneTransactionArgs,
    TransactionQueryQueryVariables,
    ResultSuccess,
    PictureWhereUniqueInput,
    PictureUpdateInput,
    LoginStreak,
    LoginStreakConfigQueryVariables,
    QueryLoginStreakConfigArgs,
} from './generated/graphql';
import { ConversationSelect } from './graphql/queries/conversation.query';
import { FriendshipsSelect } from './graphql/queries/friendships.query';
import { MyProfileSelect } from './graphql/mutations/updateMyProfile.mutation';
import {
    LoginArgs,
    LoginByTokenArgs,
    LoginSelect,
} from './graphql/mutations/login.mutation';
import { SupportTicketsSelect } from './graphql/queries/supportTickets.query';
import { SupportTicketSelect } from './graphql/queries/SupportTicket.query';
import { ProfileSelect } from './graphql/queries/profile.query';
import { ProfilesSelect } from './graphql/queries/profiles.query';
import { OffersSelect } from './graphql/queries/offers.query';
import { FavoritesSelect } from './graphql/queries/favorites.query';
import { GiftsSelect } from './graphql/queries/gifts.query';
import { NotificationsSelect } from './graphql/queries/notifications.query';
import { ProvidersSelect } from './graphql/queries/providers.query';
import { VisitSelect } from './graphql/queries/visit.query';
import { AggregateConversationsSelect } from './graphql/queries/aggregateConversations.query';
import { VisitProfileSelect } from './graphql/mutations/visitProfile.mutation';
import { InterestCategoriesSelect } from './graphql/queries/interestCategories.query';
import { ComplainTypesSelect } from './graphql/queries/complainTypes.query';
import { FacebookLoginSelect } from './graphql/mutations/facebookLogin.mutation';
import { GoogleLoginSelect } from './graphql/mutations/googleLogin.mutation';
import { BlockedSelect } from './graphql/queries/blocked.query';

export declare type Options = {
    endpoint: string;
};

export const sdkCache = async (): Promise<InMemoryCache> => {
    const cache = new InMemoryCache({
        typePolicies: {
            Profile: {
                keyFields: ['profileId'],
            },
            Theme: {
                keyFields: ['themeId'],
            },
            Avatar: {
                keyFields: ['avatarId'],
            },
            Blocked: {
                keyFields: ['blockedId'],
            },
            Conversation: {
                keyFields: ['conversationId'],
            },
            MyProfile: {
                keyFields: ['profileId'],
            },
            Gift: {
                keyFields: ['giftId'],
            },
            GiftCategory: {
                keyFields: ['giftCategoryId'],
            },
            Image: {
                keyFields: ['imageId'],
            },
        },
    });
    await persistCache({
        cache,
        storage: new LocalStorageWrapper(window.localStorage),
    });
    return cache;
};

export class SDK {
    private readonly projectName: string;
    private options: Options;
    private readonly errorCallback: (message: unknown) => unknown;
    public client: ApolloClient<NormalizedCacheObject>;
    static async Init(
        options: Options,
        errorCallback: (message: unknown) => unknown,
    ): Promise<SDK> {
        const cache = await sdkCache();
        return new SDK(options, cache, errorCallback);
    }

    constructor(
        options: Options,
        cache: InMemoryCache,
        errorCallback: (message: unknown) => unknown,
        projectName: string = 'default',
    ) {
        this.projectName = projectName;
        this.errorCallback = errorCallback;
        this.options = options;
        const httpUri = options.endpoint;
        const wsUri = httpUri.replace(/^https?/, 'wss');

        const httpLink = new HttpLink({
            uri: httpUri,
        });

        const wsLink = new WebSocketLink({
            uri: wsUri,
            options: {
                reconnect: true,
                connectionParams: () => {
                    return { Authorization: this.getAuthHeader() };
                },
            },
        });

        const authLink = setContext((_, { headers }) => {
            const auth = this.getAuthHeader();

            return {
                headers: {
                    ...headers,
                    Authorization: auth ? auth : '',
                    'x-necom-project': projectName,
                    'x-necom-user-id': localStorage.getItem('userId') || '',
                    'x-necom-method': _.operationName,
                },
            };
        });

        const afterwareLink = new ApolloLink((operation, forward) => {
            return forward(operation).map((response) => {
                const context = operation.getContext();
                const {
                    response: { headers },
                } = context;

                if (headers) {
                    const authHeader = headers.get('Authorization');
                    if (authHeader) {
                        const token = authHeader.replace('Bearer ', '');
                        this.storeAuthHeader(token);
                    }
                }
                return response;
            });
        });

        const persistedQueriesLink = createPersistedQueryLink({ sha256 });

        const terminatingLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            from([persistedQueriesLink, authLink, afterwareLink, httpLink]),
        );

        this.client = new ApolloClient({
            link: terminatingLink,
            cache,
        });
    }

    googleLogin = async (
        data: SocialLoginInput,
        select: GoogleLoginSelect = {},
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ): Promise<SocialLoginResult | ResultSuccess> => {
        const params = this.buildParams<
            GoogleLoginSelect,
            { data: SocialLoginInput },
            GoogleLoginMutationVariables
        >(select, `googleLogin`, { data: data });
        const mutation = require('./graphql/mutations/googleLogin.mutation.gql');
        const result = await this.mutate<
            {
                googleLogin: SocialLoginResult;
            },
            GoogleLoginMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            `googleLogin`,
            false,
        );
        if (result.__typename === 'LoginResultSuccess') {
            const { token, myProfile, userId } = result;
            this.login(token, myProfile, userId, subscriptionCallbacks);
        }
        return result;
    };

    changeMatchFilter = async (
        data: MatchFilterUpdateInput,
    ): Promise<MatchFilterResult | ResultSuccess> => {
        const params = this.buildParams<
            undefined,
            { data: MatchFilterUpdateInput },
            ChangeMatchFilterMutationVariables
        >(undefined, `changeMatchFilter`, { data: data });
        const mutation = require('./graphql/mutations/matchFilter.mutation.gql');
        return this.mutate<
            {
                changeMatchFilter: MatchFilterResult;
            },
            ChangeMatchFilterMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            `changeMatchFilter`,
        );
    };

    validate = async (data: {
        email?: string;
        username?: string;
        mobileNumber?: string;
        password?: string;
    }): Promise<{
        email?: ValidateResult | ResultSuccess;
        username?: ValidateResult | ResultSuccess;
        mobileNumber?: ValidateResult | ResultSuccess;
        password?: ResultSuccess | ResultError;
    }> => {
        const result: {
            email?: ValidateResult | ResultSuccess;
            username?: ValidateResult | ResultSuccess;
            mobileNumber?: ValidateResult | ResultSuccess;
            password?: ResultError | ResultSuccess;
        } = {};
        if ('email' in data) {
            if (!data.email)
                result.email = this.returnResultError([
                    ErrorCodes.EmailInvalid,
                ]);
            else {
                const mutation = require('./graphql/mutations/validateEmail.mutation.gql');
                result.email = await this.mutate<
                    { validateEmail: ValidateResult },
                    EmailMutationVariables
                >(
                    {
                        mutation: mutation,
                        variables: { e_Data: { email: data.email } },
                    },
                    'validateEmail',
                    false,
                );
            }
        }
        if ('mobileNumber' in data && data.mobileNumber) {
            if (!data.mobileNumber)
                result.mobileNumber = this.returnResultError([
                    ErrorCodes.MobilenumberInvalid,
                ]);
            else {
                const mutation = require('./graphql/mutations/validateMobileNumber.mutation.gql');
                result.mobileNumber = await this.mutate<
                    { validateMobileNumber: ValidateResult },
                    ValidateMobileNumberMutationVariables
                >(
                    {
                        mutation: mutation,
                        variables: {
                            v_Data: { mobileNumber: data.mobileNumber },
                        },
                    },
                    'validateMobileNumber',
                    false,
                );
            }
        }
        if ('username' in data) {
            if (!data.username || data.username.length < 5)
                result.username = this.returnResultError([
                    ErrorCodes.UsernameMinChars,
                ]);
            else if (data.username.length > 100)
                result.username = this.returnResultError([
                    ErrorCodes.UsernameMaxChars,
                ]);
            else {
                const mutation = require('./graphql/mutations/validateUsername.mutation.gql');
                result.username = await this.mutate<
                    { validateUsername: ValidateResult },
                    UsernameMutationVariables
                >(
                    {
                        mutation: mutation,
                        variables: { u_Data: { username: data.username } },
                    },
                    'validateUsername',
                    false,
                );
            }
        }
        if ('password' in data) {
            if (!data.password || data.password.length < 6)
                result.password = this.returnResultError([
                    ErrorCodes.PasswordMinChars,
                ]);
            else if (data.password.length > 100)
                result.password = this.returnResultError([
                    ErrorCodes.PasswordMaxChars,
                ]);
            else {
                result.password = {
                    success: true,
                    __typename: 'ResultSuccess',
                };
            }
        }
        return result;
    };

    facebookLogin = async (
        data: SocialLoginInput,
        select: FacebookLoginSelect = {},
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ): Promise<SocialLoginResult | ResultSuccess> => {
        const params = this.buildParams<
            FacebookLoginSelect,
            { data: SocialLoginInput },
            FacebookLoginMutationVariables
        >(select, `facebookLogin`, { data: data });
        const mutation = require('./graphql/mutations/facebookLogin.mutation.gql');
        const result = await this.mutate<
            {
                facebookLogin: SocialLoginResult;
            },
            FacebookLoginMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            `facebookLogin`,
            false,
        );
        if (result.__typename === 'LoginResultSuccess') {
            const { token, myProfile, userId } = result;
            this.login(token, myProfile, userId, subscriptionCallbacks);
        }
        return result;
    };

    getTransaction = async (
        transactionId: string,
    ): Promise<Transaction | ResultSuccess> => {
        const params: TransactionQueryQueryVariables = this.buildParams<
            undefined,
            QueryFindOneTransactionArgs,
            TransactionQueryQueryVariables
        >(undefined, 'Transaction', {
            where: { transactionId: transactionId },
        });
        const query = require('./graphql/queries/transactions.query.gql');
        return this.query<
            {
                findOneTransaction: Transaction;
            },
            TransactionQueryQueryVariables
        >(
            {
                query: query,
                variables: params,
            },
            'findOneTransaction',
        );
    };

    getMatchFilter = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<MatchFilter | ResultSuccess> => {
        const query = require('./graphql/queries/matchFilter.query.gql');
        return this.query<{ getMatchFilter: MatchFilter }, object>(
            { query: query, fetchPolicy: fetchPolicy },
            'getMatchFilter',
        );
    };

    matchConfig = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<MatchConfigResult | ResultSuccess> => {
        const query = require('./graphql/queries/matchConfig.query.gql');
        return this.query<{ matchConfig: MatchConfigResult }, object>(
            { query: query, fetchPolicy: fetchPolicy },
            'matchConfig',
        );
    };

    userPremiumStatus = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<UserPremiumStatus> | ResultSuccess> => {
        const query = require('./graphql/queries/userPremiumStatus.query.gql');
        return this.query<
            { userPremiumStatus: Array<UserPremiumStatus> },
            object
        >({ query: query, fetchPolicy: fetchPolicy }, 'userPremiumStatus');
    };

    getNextMatches = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<NextMatchResult | ResultSuccess> => {
        const query = require('./graphql/queries/getNextMatches.query.gql');
        return this.query<{ getNextMatches: NextMatchResult }, object>(
            { query: query, fetchPolicy: fetchPolicy },
            'getNextMatches',
        );
    };

    findManyMatch = async (
        args?: QueryFindManyMatchArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Match> | ResultSuccess> => {
        const params: MatchesQueryVariables = this.buildParams<
            undefined,
            QueryFindManyMatchArgs,
            MatchesQueryVariables
        >(undefined, 'Matches', args);
        const query = require('./graphql/queries/matches.query.gql');
        return this.query<
            {
                findManyMatch: Array<Match>;
            },
            MatchesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyMatch',
        );
    };

    findManyMatchCount = async (
        args?: QueryFindManyMatchCountArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params: MatchCountQueryVariables = this.buildParams<
            undefined,
            QueryFindManyMatchCountArgs,
            MatchCountQueryVariables
        >(undefined, 'MatchCount', args);
        const query = require('./graphql/queries/matchCount.query.gql');
        return this.query<
            {
                findManyMatchCount: Scalars['Int']['output'];
            },
            MatchCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyMatchCount',
        );
    };

    sendMatch = async (
        args?: MutationSendMatchArgs,
    ): Promise<Match | ResultSuccess> => {
        const params: SendMatchMutationVariables = this.buildParams<
            undefined,
            MutationSendMatchArgs,
            SendMatchMutationVariables
        >(undefined, 'SendMatch', args);
        const mutation = require('./graphql/mutations/sendMatch.mutation.gql');
        return this.mutate<
            {
                sendMatch: Match;
            },
            SendMatchMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            `sendMatch`,
        );
    };

    findManyPicture = async (
        args?: QueryFindManyPictureArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Picture> | ResultSuccess> => {
        const params: PicturesQueryVariables = this.buildParams<
            undefined,
            QueryFindManyPictureArgs,
            PicturesQueryVariables
        >(undefined, 'Picture', args);
        const query = require('./graphql/queries/pictures.query.gql');
        return this.query<
            {
                findManyPicture: Array<Picture>;
            },
            PicturesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyPicture',
        );
    };

    findManyBlocked = async (
        args?: QueryFindManyBlockedArgs,
        select?: { select: BlockedSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Blocked> | ResultSuccess> => {
        const params: BlockedQueryVariables = this.buildParams<
            BlockedSelect,
            QueryFindManyBlockedArgs,
            BlockedQueryVariables
        >(select?.select, 'Blocked', args);
        const query = require('./graphql/queries/blocked.query.gql');
        return this.query<
            {
                findManyBlocked: Array<Blocked>;
            },
            BlockedQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyBlocked',
        );
    };

    findManyFlag = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Flag> | ResultSuccess> => {
        const query = require('./graphql/queries/flags.query.gql');
        return this.query<
            {
                findManyFlag: Array<Flag>;
            },
            object
        >(
            {
                query: query,
                fetchPolicy,
            },
            'findManyFlag',
        );
    };

    findManyBlockedCount = async (
        args?: QueryFindManyBlockedCountArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params: BlockedQueryVariables = this.buildParams<
            undefined,
            QueryFindManyBlockedCountArgs,
            BlockedCountQueryVariables
        >(undefined, 'BlockedCount', args);
        const query = require('./graphql/queries/blockedCount.query.gql');
        return this.query<
            {
                findManyBlockedCount: Scalars['Int']['output'];
            },
            BlockedCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyBlockedCount',
        );
    };

    loginStreakConfig = async (
        args?: QueryLoginStreakConfigArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<LoginStreak | ResultSuccess> => {
        const params: LoginStreakConfigQueryVariables = this.buildParams<
            undefined,
            QueryLoginStreakConfigArgs,
            LoginStreakConfigQueryVariables
        >(undefined, 'LoginStreakConfig', args);
        const query = require('./graphql/queries/loginStreak.query.gql');
        return this.query<
            { loginStreakConfig: LoginStreak },
            LoginStreakConfigQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy: fetchPolicy,
            },
            'loginStreakConfig',
        );
    };

    updateMobileNumber = async (
        data: MobileNumberInput,
    ): Promise<ResultSuccess | ValidateResult> => {
        const mutation = require('./graphql/mutations/updateMobileNumber.mutation.gql');
        return this.mutate<
            {
                updateMobileNumber: ValidateResult;
            },
            UpdateMobileNumberMutationVariables
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'updateMobileNumber',
        );
    };

    updateEmail = async (
        data: ValidateEmailInput,
    ): Promise<ResultSuccess | ValidateResult> => {
        const mutation = require('./graphql/mutations/updateEmail.mutation.gql');
        return this.mutate<
            {
                updateEmail: ValidateResult;
            },
            UpdateEmailMutationVariables
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'updateEmail',
        );
    };

    mobileNumberValidation = async (
        data: MobileNumberValidationInput,
    ): Promise<ResultSuccess | Result> => {
        const mutation = require('./graphql/mutations/mobileNumberValidation.mutation.gql');
        return this.mutate<
            {
                mobileNumberValidation: Result;
            },
            MobileNumberValidationMutationVariables
        >(
            {
                mutation: mutation,
                variables: { m_Data: data },
            },
            'mobileNumberValidation',
        );
    };

    validateMobileNumber = async (
        data: ValidateMobileNumberInput,
    ): Promise<ResultSuccess | ValidateResult> => {
        const mutation = require('./graphql/mutations/validateMobileNumber.mutation.gql');
        return this.mutate<
            {
                validateMobileNumber: ValidateResult;
            },
            ValidateMobileNumberMutationVariables
        >(
            {
                mutation: mutation,
                variables: { v_Data: data },
            },
            'validateMobileNumber',
        );
    };

    contactSupport = async (
        data: ContactSupportInput,
    ): Promise<ResultSuccess> => {
        const mutation = require('./graphql/mutations/contactSupport.mutation.gql');
        return this.mutate<
            {
                contactSupport: ResultSuccess;
            },
            ContactSupportMutationVariables
        >(
            {
                mutation: mutation,
                variables: { c_Data: data },
            },
            'contactSupport',
            false,
        );
    };

    deleteAccount = async (
        data: DeleteAccountInput,
    ): Promise<DeleteAccountResult> => {
        const mutation = require('./graphql/mutations/deleteAccount.mutation.gql');
        return this.mutate<
            {
                deleteAccount: DeleteAccountResult;
            },
            DeleteAccountMutationVariables
        >(
            {
                mutation: mutation,
                variables: { d_Data: data },
            },
            'deleteAccount',
            false,
        );
    };

    visitProfile = async (
        data: ProfileWhereUniqueInput,
        select?: { select: VisitProfileSelect },
    ): Promise<ResultSuccess | VisitProfileResult> => {
        const params: VisitProfileMutationVariables = this.buildParams<
            VisitProfileSelect,
            MutationVisitProfileArgs,
            VisitProfileMutationVariables
        >(select?.select, 'VisitProfile', { data: data });
        const mutation = require('./graphql/mutations/visitProfile.mutation.gql');
        return this.mutate<
            {
                visitProfile: VisitProfileResult;
            },
            VisitProfileMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            'visitProfile',
        );
    };

    sendMessage = async (
        data: MessageInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        const mutation = require('./graphql/mutations/sendMessage.mutation.gql');
        return this.mutate<
            {
                sendMessage: SendMessageResult;
            },
            {
                s_Data: MessageInput;
            }
        >(
            {
                mutation: mutation,
                variables: { s_Data: data },
            },
            `sendMessage`,
        );
    };

    sendPoke = async (
        data: PokeInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        const mutation = require('./graphql/mutations/sendPoke.mutation.gql');
        return this.mutate<
            {
                sendPoke: SendMessageResult;
            },
            {
                s_Data: PokeInput;
            }
        >(
            {
                mutation: mutation,
                variables: { s_Data: data },
            },
            `sendPoke`,
        );
    };

    sendGift = async (
        data: GiftInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        const mutation = require('./graphql/mutations/sendGift.mutation.gql');
        return this.mutate<
            {
                sendGift: SendMessageResult;
            },
            {
                s_Data: GiftInput;
            }
        >(
            {
                mutation: mutation,
                variables: { s_Data: data },
            },
            `sendGift`,
        );
    };

    sendPicture = async (
        data: PictureInput,
    ): Promise<SendMessageResult | ResultSuccess> => {
        const mutation = require('./graphql/mutations/sendPicture.mutation.gql');
        return this.mutate<
            {
                sendPicture: SendMessageResult;
            },
            {
                s_Data: PictureInput;
            }
        >(
            {
                mutation: mutation,
                variables: { s_Data: data },
            },
            `sendPicture`,
        );
    };

    send = async <T extends MessageType>(
        type: T,
        data: T extends 'Message'
            ? MessageInput
            : T extends 'Poke'
              ? PokeInput
              : T extends 'Gift'
                ? GiftInput
                : T extends 'Picture'
                  ? PictureInput
                  : never,
    ): Promise<SendMessageResult | ResultSuccess> => {
        switch (type) {
            case 'Message':
                return this.sendMessage(data as MessageInput);
            case 'Poke':
                return this.sendPoke(data as PokeInput);
            case 'Gift':
                return this.sendGift(data as GiftInput);
            case 'Picture':
                return this.sendPicture(data as PictureInput);
        }
        return { success: false, __typename: 'ResultSuccess' };
    };

    updateUser = async (
        data: UserUpdateInput,
    ): Promise<User | ResultSuccess> => {
        const mutation = require('./graphql/mutations/updateUser.mutation.gql');
        return this.mutate<
            {
                updateOneUser: User;
            },
            UpdateUserMutationVariables
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'updateOneUser',
        );
    };

    createTransaction = async (
        data: TransactionInput,
    ): Promise<TransactionResult | ResultSuccess> => {
        if (!data || !('Offer' in data) || !data.Offer.offerId) {
            this.error('Wrong param for createTransaction');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/createTransaction.mutation.gql');
        return this.mutate<
            {
                createTransaction: TransactionResult;
            },
            TransactionMutationVariables
        >(
            {
                mutation: mutation,
                variables: { t_Data: data },
            },
            'createTransaction',
        );
    };

    changeFriendship = async (
        profileId: string,
        status: FriendshipStatus,
    ): Promise<ResultSuccess> => {
        if (!profileId || !(status in FriendshipStatus)) {
            this.error('Wrong param for changeFriendship');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/changeStatusFriendship.mutation.gql');
        return await this.mutate<
            {
                changeStatusFriendship: ResultSuccess;
            },
            ChangeFriendshipMutationVariables
        >(
            {
                mutation: mutation,
                variables: { c_Data: { otherId: profileId, status: status } },
            },
            'changeStatusFriendship',
        );
    };

    removeFriendship = async (profileId: string): Promise<ResultSuccess> => {
        if (!profileId) {
            this.error('Missing param profileId for removeFriendship');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/removeFriendship.mutation.gql');
        return await this.mutate<
            {
                removeFriendship: ResultSuccess;
            },
            RemoveFriendMutationVariables
        >(
            {
                mutation: mutation,
                variables: { r_Data: { otherId: profileId } },
            },
            'removeFriendship',
        );
    };

    addFriendship = async (
        profileId: string,
    ): Promise<AddFriendshipResult | ResultSuccess> => {
        if (!profileId) {
            this.error('Missing param profileId for addFriendship');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/addFriendship.mutation.gql');
        return await this.mutate<
            {
                addFriendship: AddFriendshipResult;
            },
            AddFriendMutationVariables
        >(
            {
                mutation: mutation,
                variables: { a_Data: { profileId: profileId } },
            },
            'addFriendship',
        );
    };

    forgottenPassword = async (data: {
        email?: string;
        username?: string;
    }): Promise<ResultSuccess> => {
        const mutation = require('./graphql/mutations/forgottenPassword.mutation.gql');
        return this.mutate<
            {
                forgottenPassword: ResultSuccess;
            },
            ForgottenPasswordMutationVariables
        >(
            {
                mutation: mutation,
                variables: { f_Email: data.email, f_Username: data.username },
            },
            'forgottenPassword',
            false,
        );
    };

    resetPassword = async (
        data: ResetPasswordInput,
    ): Promise<ResultSuccess | ResetPasswordResult> => {
        let signIn = true;
        if ('passwordHash' in data && data.passwordHash) signIn = false;
        const mutation = require('./graphql/mutations/resetPassword.mutation.gql');
        return this.mutate<
            {
                resetPassword: ResetPasswordResult;
            },
            {
                r_Data: ResetPasswordInput;
            }
        >(
            {
                mutation: mutation,
                variables: { r_Data: data },
            },
            'resetPassword',
            signIn,
        );
    };

    readNotifications = async (
        where: Array<NotificationWhereUniqueInput>,
    ): Promise<ResultSuccess | BatchPayload> => {
        const mutation = require('./graphql/mutations/readManyNotification.mutation.gql');
        return this.mutate<
            {
                readManyNotification: BatchPayload;
            },
            {
                r_Where: Array<NotificationWhereUniqueInput>;
            }
        >(
            {
                mutation: mutation,
                variables: { r_Where: where },
            },
            'readManyNotification',
        );
    };

    deleteImage = async (
        data: DeleteGalleryImageInput,
    ): Promise<ResultSuccess> => {
        const mutation = require('./graphql/mutations/deleteGalleryImage.mutation.gql');
        return this.mutate<
            {
                deleteGalleryImage: ResultSuccess;
            },
            {
                d_Data: DeleteGalleryImageInput;
            }
        >(
            {
                mutation: mutation,
                variables: { d_Data: data },
            },
            'deleteGalleryImage',
        );
    };

    findOneSupportTicket = async (
        args?: QueryFindOneSupportTicketArgs,
        select?: { select: SupportTicketSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<SupportTicket | ResultSuccess> => {
        const params = this.buildParams<
            SupportTicketSelect,
            QueryFindOneSupportTicketArgs,
            SupportTicketQueryVariables
        >(select?.select, 'SupportTicket', args);
        console.log(params);
        const query = require('./graphql/queries/supportTicket.query.gql');
        return this.query<
            {
                findOneSupportTicket: SupportTicket;
            },
            SupportTicketQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findOneSupportTicket',
        );
    };

    findManySupportTicket = async (
        args: QueryFindManySupportTicketArgs = {},
        select?: SupportTicketsSelect,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<SupportTicket> | ResultSuccess> => {
        if (!args.take || args.take > 10) args.take = 10;
        if (args.take < -10) args.take = -10;
        const params = this.buildParams<
            SupportTicketsSelect,
            QueryFindManySupportTicketArgs,
            SupportTicketsQueryVariables
        >(select, 'SupportTickets', args);
        const query = require('./graphql/queries/supportTickets.query.gql');
        return this.query<
            {
                findManySupportTicket: Array<SupportTicket>;
            },
            SupportTicketsQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManySupportTicket',
        );
    };

    uploadPicture = async (
        data: UploadPictureInput,
    ): Promise<ResultSuccess | Picture> => {
        const mutation = require('./graphql/mutations/uploadPicture.mutation.gql');
        return this.mutate<
            {
                uploadPicture: Picture;
            },
            {
                u_Data: UploadPictureInput;
            }
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'uploadPicture',
        );
    };

    updatePicture = async (
        data: PictureUpdateInput,
        where: PictureWhereUniqueInput,
    ): Promise<ResultSuccess | Picture> => {
        const mutation = require('./graphql/mutations/updatePicture.mutation.gql');
        return this.mutate<
            {
                updatePicture: Picture;
            },
            {
                u_Data: PictureUpdateInput;
                u_Where: PictureWhereUniqueInput;
            }
        >(
            {
                mutation: mutation,
                variables: { u_Data: data, u_Where: where },
            },
            'updatePicture',
        );
    };

    uploadImage = async (
        data: UploadGalleryImageInput,
    ): Promise<ResultSuccess | Image> => {
        //ToDo: cropper.js?
        const mutation = require('./graphql/mutations/uploadGalleryImage.mutation.gql');
        return this.mutate<
            {
                uploadGalleryImage: Image;
            },
            {
                u_Data: UploadGalleryImageInput;
            }
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'uploadGalleryImage',
        );
    };

    updateProfileImage = async (
        data: UpdateProfileImageInput,
    ): Promise<ResultSuccess> => {
        const mutation = require('./graphql/mutations/updateProfileImage.mutation.gql');
        return this.mutate<
            {
                updateProfileImage: ResultSuccess;
            },
            {
                u_Data: UpdateProfileImageInput;
            }
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'updateProfileImage',
        );
    };

    updateGalleryImage = async (
        data: UpdateGalleryImageInput,
    ): Promise<ResultSuccess> => {
        const mutation = require('./graphql/mutations/updateGalleryImage.mutation.gql');
        return this.mutate<
            {
                updateGalleryImage: ResultSuccess;
            },
            {
                u_Data: UpdateGalleryImageInput;
            }
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'updateGalleryImage',
        );
    };

    removeFavorite = async (
        profileId: string,
    ): Promise<Favorite | ResultSuccess> => {
        if (!profileId) {
            this.error('Missing param profileId for removeFavorite');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/removeFavorite.mutation.gql');
        return await this.mutate<
            {
                removeFavorite: Favorite;
            },
            RemoveFavoriteMutationVariables
        >(
            {
                mutation: mutation,
                variables: {
                    r_Data: {
                        selfId_otherId: { otherId: profileId, selfId: '' },
                    },
                },
            },
            'removeFavorite',
        );
    };

    addFavorite = async (
        profileId: string,
    ): Promise<Favorite | ResultSuccess> => {
        if (!profileId) {
            this.error('Missing param profileId for addFavorite');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/addFavorite.mutation.gql');
        return this.mutate<
            {
                addFavorite: Favorite;
            },
            AddFavoriteMutationVariables
        >(
            {
                mutation: mutation,
                variables: { a_Data: { profileId: profileId } },
            },
            'addFavorite',
        );
    };

    sendSupportMessage = async (
        data: SupportMessageCreateInput,
    ): Promise<SupportMessage | ResultSuccess> => {
        const mutation = require('./graphql/mutations/supportMessage.mutation.gql');
        return this.mutate<
            {
                sendSupportMessage: SupportMessage;
            },
            SendSupportMessageMutationVariables
        >(
            {
                mutation: mutation,
                variables: { s_Data: data },
            },
            'sendSupportMessage',
        );
    };

    updateMyProfile = async (
        data: UpdateMyProfileInput,
        select?: MyProfileSelect,
    ): Promise<UpdateMyProfileResult | ResultSuccess> => {
        const params = this.buildParams<
            MyProfileSelect,
            { data: UpdateMyProfileInput },
            UpdateMyProfileMutationVariables
        >(select, 'UpdateMyProfile', { data: data });
        const mutation = require('./graphql/mutations/updateMyProfile.mutation.gql');
        return this.mutate<
            {
                updateMyProfile: UpdateMyProfileResult;
            },
            UpdateMyProfileMutationVariables
        >(
            {
                mutation: mutation,
                variables: params,
            },
            'updateMyProfile',
        );
    };

    redeemCoupon = async (
        code: string,
    ): Promise<CoinsResult | ResultSuccess> => {
        if (!code) {
            this.error('Missing param code for redeemCoupon');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/redeemCoupon.mutation.gql');
        return this.mutate<
            {
                redeemCoupon: CoinsResult;
            },
            RedeemCouponMutationVariables
        >(
            {
                mutation: mutation,
                variables: { r_Data: { code: code } },
            },
            'redeemCoupon',
        );
    };

    unblockProfile = async (
        profileId?: string,
        blockedId?: string,
    ): Promise<ResultSuccess> => {
        const data: MyBlockedWhereUniqueInput = {};
        if (profileId) data.profileId = profileId;
        else if (blockedId) data.blockedId = blockedId;
        else {
            this.error('Missing params for unblockProfile');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/unblockProfile.mutation.gql');
        return this.mutate<
            {
                unblockProfile: ResultSuccess;
            },
            UnblockProfileMutationVariables
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'unblockProfile',
        );
    };

    readConversation = async (
        conversationId: string,
    ): Promise<ResultSuccess> => {
        if (!conversationId) {
            this.error('Missing params for readConversation');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/readConversation.mutation.gql');
        return this.mutate<
            {
                readConversation: ResultSuccess;
            },
            ReadConversationMutationVariables
        >(
            {
                mutation: mutation,
                variables: { r_ConversationId: conversationId },
            },
            'readConversation',
        );
    };

    hideConversation = async (
        conversationId: string,
        hide = true,
    ): Promise<ResultSuccess> => {
        if (!conversationId) {
            this.error('Missing params for hideConversation');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/hideConversation.mutation.gql');
        return this.mutate<
            {
                hideConversation: ResultSuccess;
            },
            HideConversationMutationVariables
        >(
            {
                mutation: mutation,
                variables: { r_ConversationId: conversationId, r_Hide: hide },
            },
            'hideConversation',
        );
    };

    blockProfile = async (data: BlockProfileInput): Promise<ResultSuccess> => {
        if (!data) {
            this.error('Missing params for blockProfile');
            return { __typename: 'ResultSuccess', success: false };
        }
        const mutation = require('./graphql/mutations/blockProfile.mutation.gql');
        return this.mutate<
            {
                blockProfile: ResultSuccess;
            },
            BlockProfileMutationVariables
        >(
            {
                mutation: mutation,
                variables: { b_Data: data },
            },
            'blockProfile',
        );
    };

    findOneConversation = async (
        args?: QueryFindOneConversationArgs,
        select?: { select: ConversationSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Conversation | ResultSuccess> => {
        const ownProfileId = localStorage.getItem('myProfileId');
        if (ownProfileId && select) {
            const params: ConversationQueryVariables = this.buildParams<
                ConversationSelect,
                QueryFindOneConversationArgs,
                ConversationQueryVariables
            >(select?.select, 'Conversation', args);
            params.ownProfileId = ownProfileId;
            const conversationQuery = require('./graphql/queries/conversation.query.gql');
            return this.query<
                {
                    findOneConversation: Conversation;
                },
                ConversationQueryVariables
            >(
                {
                    query: conversationQuery,
                    variables: params,
                    fetchPolicy,
                },
                'findOneConversation',
            );
        }
        return { __typename: 'ResultSuccess', success: false };
    };

    unreadSupportMessageCount = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const supportMessageQuery = require('./graphql/queries/supportMessage.query.gql');
        return this.query<{
            unreadSupportMessageCount: Scalars['Int']['output'];
        }>(
            {
                query: supportMessageQuery,
                fetchPolicy,
            },
            'unreadSupportMessageCount',
        );
    };

    findManyFriendship = async (
        args: QueryFindManyFriendshipArgs = {},
        select?: { select: FriendshipsSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Friendship> | ResultSuccess> => {
        if (!args.take || args.take > 10) args.take = 10;
        if (args.take < -10) args.take = -10;
        const params: FriendshipsQueryVariables = this.buildParams<
            FriendshipsSelect,
            QueryFindManyFriendshipArgs,
            FriendshipsQueryVariables
        >(select?.select, 'Friendship', args);
        const query = require('./graphql/queries/friendships.query.gql');
        return this.query<
            {
                findManyFriendship: Array<Friendship>;
            },
            FriendshipsQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyFriendship',
        );
    };

    findManyFriendshipCount = async (
        args: QueryFindManyFriendshipCountArgs = {},
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params: FriendshipsCountQueryVariables = this.buildParams<
            undefined,
            QueryFindManyFriendshipCountArgs,
            FriendshipsCountQueryVariables
        >(undefined, 'FriendshipCount', args);
        const query = require('./graphql/queries/friendshipsCount.query.gql');
        return this.query<
            {
                findManyFriendshipCount: Scalars['Int']['output'];
            },
            FriendshipsCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyFriendshipCount',
        );
    };

    findManyConversation = async (
        args: QueryFindManyConversationArgs = {},
        select?: { select: ConversationsSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Conversation> | ResultSuccess> => {
        if (!args.take || args.take > 10) args.take = 10;
        if (args.take < -10) args.take = -10;
        const ownProfileId = localStorage.getItem('myProfileId');
        if (ownProfileId) {
            const params: ConversationsQueryVariables = this.buildParams<
                ConversationsSelect,
                QueryFindManyConversationArgs,
                ConversationsQueryVariables
            >(select?.select, 'Conversation', args);
            params.ownProfileId = ownProfileId;
            const conversationsQuery = require('./graphql/queries/conversations.query.gql');
            return this.query<
                {
                    findManyConversation: Array<Conversation>;
                },
                ConversationsQueryVariables
            >(
                {
                    query: conversationsQuery,
                    variables: params,
                    fetchPolicy,
                },
                'findManyConversation',
            );
        }
        return { __typename: 'ResultSuccess', success: false };
    };

    findOneProfile = async (
        args?: QueryFindOneProfileArgs,
        select?: { select: ProfileSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Profile | ResultSuccess> => {
        const params: ProfileQueryVariables = this.buildParams<
            ProfileSelect,
            QueryFindOneProfileArgs,
            ProfileQueryVariables
        >(select?.select, 'Profile', args);
        const query = require('./graphql/queries/profile.query.gql');
        return this.query<
            {
                findOneProfile: Profile;
            },
            ProfileQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findOneProfile',
            false,
        );
    };

    findManyComplainType = async (
        select?: { select: ComplainTypesSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<ComplainType> | ResultSuccess> => {
        const params = this.buildParams<
            ComplainTypesSelect,
            unknown,
            ComplainTypesQueryVariables
        >(select?.select, 'ComplainType', null);
        const query = require('./graphql/queries/complainTypes.query.gql');
        return this.query<
            {
                findManyComplainType: Array<ComplainType>;
            },
            ComplainTypesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyComplainType',
        );
    };

    findManyProfileCount = async (
        args?: QueryFindManyProfileCountArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params = this.buildParams<
            unknown,
            QueryFindManyProfileCountArgs,
            ProfileCountQueryVariables
        >(null, 'ProfileCount', args);
        const query = require('./graphql/queries/profileCount.query.gql');
        return this.query<
            {
                findManyProfileCount: Scalars['Int']['output'];
            },
            ProfileCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyProfileCount',
        );
    };

    findManyProfile = async (
        args?: QueryFindManyProfileArgs,
        select?: { select: ProfilesSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Profile> | ResultSuccess> => {
        const params: ProfilesQueryVariables = this.buildParams<
            ProfilesSelect,
            QueryFindManyProfileArgs,
            ProfilesQueryVariables
        >(select?.select, 'Profile', args);
        const query = require('./graphql/queries/profiles.query.gql');
        return this.query<
            {
                findManyProfile: Array<Profile>;
            },
            ProfilesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyProfile',
            false,
        );
    };

    findManyOffer = async (
        args?: QueryFindManyOfferArgs,
        select?: { select: OffersSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Offer> | ResultSuccess> => {
        const params: OffersQueryVariables = this.buildParams<
            OffersSelect,
            QueryFindManyOfferArgs,
            OffersQueryVariables
        >(select?.select, 'Offer', args);
        const query = require('./graphql/queries/offers.query.gql');
        return this.query<
            {
                findManyOffer: Array<Offer>;
            },
            OffersQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyOffer',
        );
    };

    findManyFavorite = async (
        args?: QueryFindManyFavoriteArgs,
        select?: { select: FavoritesSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Favorite> | ResultSuccess> => {
        const params: FavoritesQueryVariables = this.buildParams<
            FavoritesSelect,
            QueryFindManyFavoriteArgs,
            FavoritesQueryVariables
        >(select?.select, 'Favorites', args);
        const query = require('./graphql/queries/favorites.query.gql');
        return this.query<
            {
                findManyFavorite: Array<Favorite>;
            },
            FavoritesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyFavorite',
        );
    };

    findManyFavoriteCount = async (
        args?: QueryFindManyFavoriteCountArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params: FavoritesCountQueryVariables = this.buildParams<
            undefined,
            QueryFindManyFavoriteArgs,
            FavoritesCountQueryVariables
        >(undefined, 'FavoritesCount', args);
        const query = require('./graphql/queries/favoritesCount.query.gql');
        return this.query<
            {
                findManyFavoriteCount: Scalars['Int']['output'];
            },
            FavoritesCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyFavoriteCount',
        );
    };

    aggregateConversations = async (
        args?: QueryAggregateConversationsArgs,
        select?: { select: AggregateConversationsSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<AggregateConversationsResult | ResultSuccess> => {
        const params = this.buildParams<
            AggregateConversationsSelect,
            QueryAggregateConversationsArgs,
            AggregateConversationsQueryVariables
        >(select?.select, 'AggregateConversationsResult', args);
        const query = require('./graphql/queries/aggregateConversations.query.gql');
        return this.query<
            {
                aggregateConversations: AggregateConversationsResult;
            },
            AggregateConversationsQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'aggregateConversations',
        );
    };

    coins = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<number | ResultSuccess> => {
        const query = require('./graphql/queries/coins.query.gql');
        return this.query<{
            coins: number;
        }>(
            {
                query: query,
                fetchPolicy,
            },
            'coins',
        );
    };

    findManyGift = async (
        args?: QueryFindManyGiftArgs,
        select?: { select: GiftsSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Gift> | ResultSuccess> => {
        const params: GiftsQueryVariables = this.buildParams<
            GiftsSelect,
            QueryFindManyGiftArgs,
            GiftsQueryVariables
        >(select?.select, 'Gift', args);
        const query = require('./graphql/queries/gifts.query.gql');
        return this.query<
            {
                findManyGift: Array<Gift>;
            },
            GiftsQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyGift',
        );
    };

    findManyNotification = async (
        args?: QueryFindManyNotificationArgs,
        select?: { select: NotificationsSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Notification> | ResultSuccess> => {
        const params: NotificationsQueryVariables = this.buildParams<
            NotificationsSelect,
            QueryFindManyNotificationArgs,
            NotificationsQueryVariables
        >(select?.select, 'Notification', args);
        const query = require('./graphql/queries/notifications.query.gql');
        return this.query<
            {
                findManyNotification: Array<Notification>;
            },
            NotificationsQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyNotification',
        );
    };

    findManyNotificationCount = async (
        args?: QueryFindManyNotificationCountArgs,
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Scalars['Int']['output'] | ResultSuccess> => {
        const params = this.buildParams<
            unknown,
            QueryFindManyNotificationCountArgs,
            NotificationCountQueryVariables
        >(null, 'NotificationCount', args);
        const query = require('./graphql/queries/notificationCount.query.gql');
        return this.query<
            {
                findManyNotificationCount: Scalars['Int']['output'];
            },
            NotificationCountQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyNotificationCount',
        );
    };

    findManyLanguage = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Language> | ResultSuccess> => {
        const query = require('./graphql/queries/languages.query.gql');
        return this.query<
            {
                findManyLanguage: Array<Language>;
            },
            object
        >(
            {
                query: query,
                fetchPolicy,
            },
            'findManyLanguage',
        );
    };

    userComplain = async (
        data: UserComplainCreateInput,
    ): Promise<UserComplain | ResultSuccess> => {
        const mutation = require('./graphql/mutations/userComplain.mutation.gql');
        return this.mutate<
            {
                createOneUserComplain: UserComplain;
            },
            UserComplainMutationVariables
        >(
            {
                mutation: mutation,
                variables: { u_Data: data },
            },
            'createOneUserComplain',
        );
    };

    readSupportMessage = async (
        where: SupportMessageWhereUniqueInput,
    ): Promise<SupportMessage | ResultSuccess> => {
        const mutation = require('./graphql/mutations/readSupportMessage.mutation.gql');
        return this.mutate<
            {
                readSupportMessage: SupportMessage;
            },
            ReadSupportMessageMutationVariables
        >(
            {
                mutation: mutation,
                variables: { r_Where: where },
            },
            'readSupportMessage',
        );
    };

    myProfile = async (
        select?: {
            select: MyProfileSelect;
        },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<MyProfile | ResultSuccess> => {
        const params: MyProfileQueryVariables = this.buildParams<
            MyProfileSelect,
            unknown,
            MyProfileQueryVariables
        >(select?.select, 'MyProfile', null);
        const query = require('./graphql/queries/myProfile.query.gql');
        return this.query<
            {
                myProfile: MyProfile;
            },
            MyProfileQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'myProfile',
        );
    };

    findManyTheme = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Theme> | ResultSuccess> => {
        const query = require('./graphql/queries/themes.query.gql');
        return this.query<
            {
                findManyTheme: Array<Theme>;
            },
            ThemesQueryVariables
        >(
            {
                query: query,
                fetchPolicy,
            },
            'findManyTheme',
        );
    };

    findManyInterestCategory = async (
        args: QueryFindManyInterestCategoryArgs,
        select: { select: InterestCategoriesSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<InterestCategory> | ResultSuccess> => {
        const params: InterestCategoriesQueryVariables = this.buildParams<
            InterestCategoriesSelect,
            QueryFindManyInterestCategoryArgs,
            InterestCategoriesQueryVariables
        >(select.select, 'InterestCategory', args);
        const query = require('./graphql/queries/interestCategories.query.gql');
        return this.query<
            {
                findManyInterestCategory: Array<InterestCategory>;
            },
            InterestCategoriesQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyInterestCategory',
        );
    };

    findManyProvider = async (
        args?: QueryFindManyProviderArgs,
        select?: { select: ProvidersSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Provider> | ResultSuccess> => {
        const params: ProvidersQueryVariables = this.buildParams<
            ProvidersSelect,
            QueryFindManyProviderArgs,
            ProvidersQueryVariables
        >(select?.select, 'Provider', args);
        const query = require('./graphql/queries/providers.query.gql');
        return this.query<
            {
                findManyProvider: Array<Provider>;
            },
            ProvidersQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyProvider',
        );
    };

    config = async (
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Config | ResultSuccess> => {
        const query = require('./graphql/queries/config.query.gql');
        return this.query<{
            config: Config;
        }>(
            {
                query: query,
                fetchPolicy,
            },
            'config',
            false,
        );
    };

    findManyVisit = async (
        args?: QueryFindManyVisitArgs,
        select?: { select: VisitSelect },
        fetchPolicy: FetchPolicy = 'cache-first',
    ): Promise<Array<Visit> | ResultSuccess> => {
        const params: VisitQueryVariables = this.buildParams<
            VisitSelect,
            QueryFindManyVisitArgs,
            VisitQueryVariables
        >(select?.select, 'Visit', args);
        const query = require('./graphql/queries/visit.query.gql');
        return this.query<
            {
                findManyVisit: Array<Visit>;
            },
            VisitQueryVariables
        >(
            {
                query: query,
                variables: params,
                fetchPolicy,
            },
            'findManyVisit',
        );
    };

    register = async (
        args: RegisterArgs,
        select: { select: RegisterSelect },
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ): Promise<RegisterResult | ResultSuccess> => {
        const params: RegisterMutationVariables = this.buildParams<
            RegisterSelect,
            RegisterArgs,
            RegisterMutationVariables
        >(select.select, 'Register', args);
        const registerMutation = require('./graphql/mutations/register.mutation.gql');
        const result = await this.mutate<
            {
                register: RegisterResult;
            },
            RegisterMutationVariables
        >({ mutation: registerMutation, variables: params }, 'register', false);

        if (result && 'token' in result && result.token && result.myProfile) {
            const { token, myProfile, userId } = result;
            this.login(token, myProfile, userId, subscriptionCallbacks);
        }
        return result;
    };

    subscribe = async (subscriptionCallbacks: {
        onNext?: (value: unknown) => void;
        onError?: (error: unknown) => void;
        onComplete?: () => void;
    }): Promise<void> => {
        const subscription = require('./graphql/subscriptions/notification.subscription.gql');
        const observable = this.client.subscribe({
            query: subscription,
        });
        observable.subscribe({
            next: (x) => {
                if (
                    subscriptionCallbacks.onNext &&
                    'function' === typeof subscriptionCallbacks.onNext
                )
                    subscriptionCallbacks.onNext(x);
                else console.log(x);
            },
            error: (err) => {
                if (
                    subscriptionCallbacks.onError &&
                    'function' === typeof subscriptionCallbacks.onError
                )
                    subscriptionCallbacks.onError(err);
                else console.log(err);
            },
            complete: () => {
                if (
                    subscriptionCallbacks.onComplete &&
                    'function' === typeof subscriptionCallbacks.onComplete
                )
                    subscriptionCallbacks.onComplete();
                else console.log('Finished');
            },
        });
    };

    getAuthHeader = (): string | null => {
        return localStorage.getItem('Authorization') || null;
    };

    storeAuthHeader = (auth: string): void => {
        localStorage.setItem('Authorization', `Bearer ${auth}`);
    };

    // Todo clear not all in cache (Gifts?)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signOut = async (clearCache?: boolean): Promise<void> => {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('myProfileId');
        localStorage.removeItem('apollo-cache-persist');
        return;
    };

    private login = (
        token: string,
        myProfile: MyProfile,
        userId: string,
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ) => {
        this.storeAuthHeader(token);
        localStorage.setItem(
            'myProfileId',
            myProfile?.profileId ? myProfile.profileId : '',
        );
        localStorage.setItem('userId', userId ? userId : '');
        const subscription = require('./graphql/subscriptions/notification.subscription.gql');
        const observable = this.client.subscribe({
            query: subscription,
        });
        observable.subscribe({
            next: (x) => {
                if (
                    subscriptionCallbacks.onNext &&
                    'function' === typeof subscriptionCallbacks.onNext
                )
                    subscriptionCallbacks.onNext(x);
                else console.log(x);
            },
            error: (err) => {
                if (
                    subscriptionCallbacks.onError &&
                    'function' === typeof subscriptionCallbacks.onError
                )
                    subscriptionCallbacks.onError(err);
                else console.log(err);
            },
            complete: () => {
                if (
                    subscriptionCallbacks.onComplete &&
                    'function' === typeof subscriptionCallbacks.onComplete
                )
                    subscriptionCallbacks.onComplete();
                else console.log('Finished');
            },
        });
    };

    signInWithToken = async (
        token: string,
        timezone: string,
        select: { select: LoginSelect },
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ): Promise<LoginResult | ResultSuccess> => {
        const params: LoginByTokenMutationVariables = this.buildParams<
            LoginSelect,
            LoginByTokenArgs,
            LoginByTokenMutationVariables
        >(select.select, 'Login', {
            token: token,
            timezone: timezone,
        });
        const loginMutation = require('./graphql/mutations/loginByToken.mutation.gql');

        const result = await this.mutate<
            {
                loginByToken: LoginResult;
            },
            LoginByTokenMutationVariables
        >(
            {
                fetchPolicy: 'no-cache',
                mutation: loginMutation,
                variables: params,
            },
            'loginByToken',
            false,
        );
        if (result.__typename === 'LoginResultSuccess') {
            const { token, myProfile, userId } = result;
            this.login(token, myProfile, userId, subscriptionCallbacks);
        }
        return result;
    };

    signIn = async (
        username: string,
        password: string,
        timezone: string,
        select: { select: LoginSelect },
        subscriptionCallbacks: {
            onNext?: (value: unknown) => void;
            onError?: (error: unknown) => void;
            onComplete?: () => void;
        },
    ): Promise<LoginResult | ResultSuccess> => {
        const params: LoginMutationVariables = this.buildParams<
            LoginSelect,
            LoginArgs,
            LoginMutationVariables
        >(select.select, 'Login', {
            username: username,
            password: password,
            timezone: timezone,
        });
        const loginMutation = require('./graphql/mutations/login.mutation.gql');

        const result = await this.mutate<
            {
                login: LoginResult;
            },
            LoginMutationVariables
        >(
            {
                fetchPolicy: 'no-cache',
                mutation: loginMutation,
                variables: params,
            },
            'login',
            false,
        );
        if (result.__typename === 'LoginResultSuccess') {
            const { token, myProfile, userId } = result;
            this.login(token, myProfile, userId, subscriptionCallbacks);
        }
        return result;
    };

    buildParams = <S, A, R>(
        select: S | undefined,
        name: string,
        args?: A,
        result: { [key: string]: unknown } = {},
        parentName = '',
    ): R => {
        let upperName,
            lowerName = '';
        if (parentName === '') {
            upperName = name[0].toUpperCase();
            lowerName = name[0].toLowerCase();
        } else {
            upperName = name[0].toUpperCase() + name.slice(1);
            lowerName = name[0].toLowerCase() + name.slice(1);
        }

        if (args) {
            for (const [key, value] of Object.entries(args)) {
                if (parentName === '') {
                    result[
                        lowerName + '_' + key[0].toUpperCase() + key.slice(1)
                    ] = value;
                } else {
                    result[
                        lowerName +
                            parentName.slice(1) +
                            key[0].toUpperCase() +
                            key.slice(1)
                    ] = value;
                }
            }
        }
        if (select) {
            for (const [key, value] of Object.entries(select)) {
                const upperKey = key[0].toUpperCase() + key.slice(1);
                if ('boolean' === typeof value) {
                    result[`with${upperName + '_' + upperKey}`] = value;
                } else if (value && 'object' === typeof value) {
                    const innerValue = value as {
                        select: unknown | undefined;
                        args?: unknown;
                    };
                    result[`with${upperName + '_' + upperKey}`] = true;
                    result = this.buildParams(
                        innerValue.select,
                        upperName + '_' + upperKey[0],
                        innerValue?.args,
                        result,
                        upperKey,
                    );
                }
            }
        }
        return result as unknown as R;
    };

    public error = (message: unknown): void => {
        console.log(`Error: ${message}`);
        if (typeof this.errorCallback === 'function')
            this.errorCallback(message);
    };

    mutate = async <
        N extends Partial<Mutation>,
        V extends OperationVariables = Record<string, unknown>,
    >(
        options: MutationOptions<N, V>,
        name: keyof N extends keyof Mutation ? keyof N : never,
        signIn = true,
    ): Promise<N[typeof name] | ResultSuccess> => {
        if (signIn && this.getAuthHeader() === null) {
            this.error('Please singIn');
        } else {
            try {
                const result = await this.client.mutate<N, V>(options);
                if (result && result.errors) this.error(result.errors);
                else if (result && result.data && name in result.data)
                    return result.data[name];
            } catch (e) {
                this.error(e);
            }
        }
        return { __typename: 'ResultSuccess', success: false };
    };

    query = async <
        N extends Partial<Query>,
        V extends OperationVariables = Record<string, unknown>,
    >(
        options: QueryOptions<V, N>,
        name: keyof N extends keyof Query ? keyof N : never,
        signIn = true,
    ): Promise<N[typeof name] | ResultSuccess> => {
        if (signIn && this.getAuthHeader() === null) {
            this.error('Please singIn');
        } else {
            try {
                const result = await this.client.query<N, V>(options);
                if (result && result.errors) this.error(result.errors);
                else if (result && result.data && name in result.data)
                    return result.data[name];
            } catch (e) {
                this.error(e);
            }
        }
        return { __typename: 'ResultSuccess', success: false };
    };

    public returnResultError = (errorCodes: Array<ErrorCodes>): ResultError => {
        const result: ResultError = {
            __typename: 'ResultError',
            errors: [],
        };
        for (const key of errorCodes) {
            result.errors?.push({
                errorCode: ErrorCodesEnum[key],
                errorMessage: key,
                __typename: 'Error',
            });
        }
        return result;
    };
}
